// Template detail keeps setup deliberate: browsing never creates or runs a task.
import React, { useEffect, useMemo, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import TemplatePinCard from '@/components/Tasks/TemplatePinCard';
import { useTaskTemplatesStore, useModalStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

export async function generateStaticParams() { return []; }

const SCHEDULE_LABEL = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  annually: 'Yearly',
};
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatTimeOfDay = (hour, minute) => {
  if (hour === undefined || hour === null) return '';
  const h12 = ((hour + 11) % 12) + 1;
  const ampm = hour < 12 ? 'AM' : 'PM';
  const mm = String(minute || 0).padStart(2, '0');
  return `${h12}:${mm} ${ampm}`;
};

const summarizeSchedule = (sched) => {
  if (!sched?.type) return '';
  const time = formatTimeOfDay(sched?.timeOfDay?.hour, sched?.timeOfDay?.minute);
  const base = SCHEDULE_LABEL[sched.type] || sched.type;
  if (sched.type === 'weekly' && Number.isFinite(sched.daysOfWeek)) {
    return `${base} on ${DAYS_OF_WEEK[sched.daysOfWeek]} at ${time}`;
  }
  if (sched.type === 'monthly' && sched.dayOfMonth) {
    return `${base} on day ${sched.dayOfMonth} at ${time}`;
  }
  if (sched.type === 'once') return 'Run once';
  return `${base} at ${time}`;
};

/** Normalize admin-authored `meta.pageCopy` into ToolPageCopy blocks. */
function normalizeTaskTemplatePageCopy(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((b) => {
        if (!b || typeof b !== 'object') return null;
        const type = String(b.type || '').trim();
        if (!type) return null;
        return { ...b, type };
      })
      .filter(Boolean);
  }
  if (typeof raw === 'object') {
    const blocks = [];
    if (raw.hero && typeof raw.hero === 'object') {
      blocks.push({ type: 'hero', ...raw.hero });
    }
    if (Array.isArray(raw.sections)) {
      raw.sections.forEach((s) => {
        if (s && typeof s === 'object') blocks.push(s);
      });
    }
    return blocks;
  }
  return [];
}

function buildFaqBlock(faq) {
  if (!Array.isArray(faq) || !faq.length) return null;
  const items = faq
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const q = String(row.q || row.question || '').trim();
      const a = String(row.a || row.answer || '').trim();
      if (!q || !a) return null;
      return { q, a };
    })
    .filter(Boolean);
  if (!items.length) return null;
  return { type: 'faq', title: 'FAQ', items };
}

export default function TaskTemplateDetailPage() {
  const params = useLocalSearchParams();
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;
  const router = useRouter();
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [width, setWidth] = useState(0);
  const [details, setDetails] = useState(false);
  const templates = useTaskTemplatesStore(s => s.templates);
  const categories = useTaskTemplatesStore(s => s.categories);
  const fetchTemplates = useTaskTemplatesStore(s => s.fetchTemplates);
  const isFetched = useTaskTemplatesStore(s => s.isFetched);
  const isLoading = useTaskTemplatesStore(s => s.isLoading);
  const { openModal } = useModalStore(s => s.actions);
  const requireAuth = useRequireAuth();
  useEffect(() => { if (!isFetched && !isLoading) fetchTemplates(); }, [isFetched, isLoading, fetchTemplates]);
  useEffect(() => { setDetails(false); }, [slug]);
  const template = templates.find(item => item.slug === slug);
  const blocks = useMemo(() => {
    const copy = normalizeTaskTemplatePageCopy(template?.meta?.pageCopy);
    const faq = buildFaqBlock(template?.meta?.faq);
    return faq ? [...copy, faq] : copy;
  }, [template]);
  const openSetup = target => requireAuth(() => openModal('SetupTaskFromTemplateModal', { template: target }), {
    source: 'tasks_template_detail_setup', title: `Sign in to set up ${target.name}`,
    subtitle: 'Choose the inputs, schedule, and cost before creating your task.', icon: 'calendar-clock',
  });
  const border = { borderColor: palette.border };
  const canonicalPath = `/tasks/template/${slug || ''}`;
  const category = categories.find(item => item.slug === template?.category);
  const related = template ? templates.filter(item => item.slug && item.slug !== slug && item.category === template.category).slice(0, 3) : [];
  const fields = Array.isArray(template?.configurableFields) ? template.configurableFields : [];
  const wide = width >= 740;
  return <View style={[styles.root, { backgroundColor: palette.control }]}>
    <MetaHead title={template ? `${template.name} · ${t('tasks.taskTemplatesTitle')}` : t('tasks.taskTemplate')}
      description={template?.description || t('tasks.templateMetaDescriptionFallback')} path={canonicalPath}
      jsonLd={template ? { '@context': 'https://schema.org', '@type': 'WebPage', name: template.name, description: template.description, url: `https://gab.ai${canonicalPath}` } : undefined} />
    <ScrollView contentContainerStyle={{ paddingBottom: insets.bottom + 48 }}>
      <View onLayout={event => setWidth(event.nativeEvent.layout.width)} style={styles.page}>
        <Pressable accessibilityRole="link" accessibilityLabel={t('tasks.allTaskTemplates')} onPress={() => router.push('/tasks/browse')} style={styles.back}>
          <Icon name="chevron-left" size={12} color={palette.icon} /><Text size="s" color="secondary">{t('tasks.allTaskTemplates')}</Text>
        </Pressable>
        {!template ? <View style={styles.empty}>
          {!isFetched || isLoading ? <><ActivityIndicator color={palette.icon} /><Text color="secondary">{t('tasks.loadingTemplate')}</Text></> : <Text size="xl">{t('tasks.templateNotFound')}</Text>}
        </View> : <>
          <View style={styles.hero}>
            <View style={styles.eyebrow}><View style={[styles.icon, { backgroundColor: palette.preview }]}><Icon name={template.icon || 'calendar-check'} size={22} color={palette.ink} /></View><Text size="s" color="secondary">{category?.name || 'Task template'}</Text></View>
            <Text accessibilityRole="header" weight="semibold" style={{ fontSize: wide ? 36 : 28, lineHeight: wide ? 44 : 35, letterSpacing: -0.8 }}>{template.name}</Text>
            <Text size="m" color="secondary" style={styles.description}>{template.description}</Text>
            <View style={styles.setupRow}>
              <Pressable accessibilityRole="button" accessibilityLabel={t('tasks.setUpThisTask')} onPress={() => openSetup(template)}
                style={({ pressed }) => [styles.primary, { backgroundColor: colors.text_color_primary, opacity: pressed ? 0.75 : 1 }]}>
                <Text weight="semibold" size="s" style={{ color: colors.solid_color_primary }}>{t('tasks.setUpThisTask')}</Text><Icon name="arrow-right" size={13} color={colors.solid_color_primary} />
              </Pressable>
              <Text size="xs" color="secondary" style={{ flexShrink: 1 }}>Review the schedule and cost before creating.</Text>
            </View>
          </View>
          <View style={[styles.overview, border, { flexDirection: wide ? 'row' : 'column' }]}>
            <View style={[styles.panel, { flex: wide ? 1 : undefined }]}>
              <Text size="l" weight="semibold">Make it yours</Text>
              <Text size="s" color="secondary" style={styles.body}>Choose what matters to you during setup.</Text>
              {fields.length ? <View style={styles.fields}>{fields.map((field, index) => <View key={field.key || index} style={styles.field}>
                <Icon name="check" size={12} color={palette.icon} /><Text size="s" style={{ flex: 1, lineHeight: 21 }}>{field.label || field.key}</Text>
              </View>)}</View> : <Text size="s" color="secondary">Add your instructions and choose when it runs.</Text>}
            </View>
            <View style={[styles.panel, wide ? styles.sidePanel : styles.lowerPanel, border, { flex: wide ? 1 : undefined }]}>
              <Text size="l" weight="semibold">On your schedule</Text>
              <View style={styles.schedule}><Icon name="clock" size={16} color={palette.icon} /><Text size="m" weight="medium" style={{ flexShrink: 1 }}>{summarizeSchedule(template.defaultSchedule) || 'Choose during setup'}</Text></View>
              <Text size="s" color="secondary" style={styles.body}>A suggested starting point. Change the timing during setup, then find each result in Tasks.</Text>
            </View>
          </View>
          {!!blocks.length && <View style={[styles.details, border]}>
            <Pressable accessibilityRole="button" accessibilityLabel="About this task" accessibilityState={{ expanded: details }} onPress={() => setDetails(value => !value)} style={styles.detailsButton}>
              <Text size="l" weight="semibold">About this task</Text><Icon name={details ? 'chevron-up' : 'chevron-down'} size={13} color={palette.icon} />
            </Pressable>
            {details && <View style={styles.detailCopy}><ToolPageCopy blocks={blocks} palette={{ bg: palette.preview, border: palette.border, fg: palette.ink, text: palette.ink }} isMD={!wide} /></View>}
          </View>}
          {!!related.length && <View style={styles.related}>
            <Text size="l" weight="semibold">{t('tasks.relatedTemplates')}</Text>
            <View style={[styles.relatedGrid, { flexDirection: wide ? 'row' : 'column' }]}>{related.map(item => <View key={item.slug} style={{ flex: wide ? 1 : undefined, minWidth: 0 }}>
              <TemplatePinCard template={item} discovery stretchHeight={wide} onPress={() => router.push(`/tasks/template/${encodeURIComponent(item.slug)}`)} onSchedulePress={() => openSetup(item)} />
            </View>)}</View>
          </View>}
        </>}
      </View>
    </ScrollView>
  </View>;
}
const styles = StyleSheet.create({
  root: { flex: 1 }, page: { width: '100%', maxWidth: 980, alignSelf: 'center', paddingHorizontal: 24, paddingTop: 24 },
  back: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 8, minHeight: 40 },
  hero: { paddingTop: 28, paddingBottom: 36, gap: 18 }, eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  icon: { width: 46, height: 46, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  description: { maxWidth: 680, lineHeight: 25 }, setupRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16, paddingTop: 6 },
  primary: { minHeight: 44, paddingHorizontal: 20, borderRadius: 12, flexDirection: 'row', alignItems: 'center', gap: 12 },
  overview: { borderWidth: 1, borderRadius: 20, paddingVertical: 24 }, panel: { paddingHorizontal: 24, gap: 12 },
  sidePanel: { borderLeftWidth: StyleSheet.hairlineWidth }, lowerPanel: { borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 24, marginTop: 24 },
  body: { lineHeight: 22 }, fields: { gap: 12, paddingTop: 4 }, field: { flexDirection: 'row', gap: 10, alignItems: 'center' },
  schedule: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 4 },
  details: { borderBottomWidth: StyleSheet.hairlineWidth, marginTop: 20 }, detailsButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 64, gap: 16 }, detailCopy: { paddingTop: 16, paddingBottom: 28 },
  related: { paddingTop: 32, gap: 20 }, relatedGrid: { gap: 14 }, empty: { alignItems: 'center', padding: 64, gap: 16 },
});
